/**
 * DataBest — AssignmentFormDrawer
 * Modal for creating a new caregiver↔individual assignment.
 * (Editing/ending existing ones is done via inline row actions.)
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, UserPlus, Info } from "lucide-react";
import DatePickerUS from "@/components/DatePickerUS";
import AssignmentRoleBadge from "./AssignmentRoleBadge";
import { assignmentsApi } from "../api/assignmentsApi";
import { individualsApi } from "@/lib/planApi";
import { usersApi } from "@/lib/orgApi";
import { formatDateUS, todayISO } from "@/lib/dates";

const ROLES = [
    { value: "primary", label: "Primary", hint: "Accountable / on shift" },
    { value: "secondary", label: "Secondary", hint: "Shared responsibility" },
    { value: "backup", label: "Backup", hint: "Covers gaps" },
];

const AssignmentFormDrawer = ({
    open,
    onOpenChange,
    defaultIndividualId = null,
    defaultCaregiverId = null,
    onSaved,
}) => {
    const [caregivers, setCaregivers] = useState([]);
    const [individuals, setIndividuals] = useState([]);
    const [caregiverId, setCaregiverId] = useState(defaultCaregiverId || "");
    const [individualId, setIndividualId] = useState(defaultIndividualId || "");
    const [role, setRole] = useState("primary");
    const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
    const [effectiveTo, setEffectiveTo] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    // Active assignments already existing for the selected individual — used
    // to (1) hide already-assigned caregivers from the dropdown and (2) show
    // context so the supervisor knows who is already involved.
    const [activeAssignments, setActiveAssignments] = useState([]);
    const [activeLoading, setActiveLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCaregiverId(defaultCaregiverId || "");
        setIndividualId(defaultIndividualId || "");
        setRole("primary");
        setEffectiveFrom(todayISO());
        setEffectiveTo("");
        setNotes("");
        setActiveAssignments([]);

        individualsApi
            .list({ active: true })
            .then((rows) => setIndividuals(rows || []))
            .catch(() => setIndividuals([]));

        usersApi
            .list({ role: "caregiver", active: true, page_size: 200 })
            .then((r) => {
                const items = Array.isArray(r) ? r : r?.items || [];
                setCaregivers(items);
            })
            .catch(() => setCaregivers([]));
    }, [open, defaultIndividualId, defaultCaregiverId]);

    // When the individual selection changes, fetch its current active
    // assignments so we can hide caregivers that are already involved.
    useEffect(() => {
        if (!open || !individualId) {
            setActiveAssignments([]);
            return;
        }
        let cancelled = false;
        setActiveLoading(true);
        assignmentsApi
            .byIndividual(individualId)
            .then((rows) => {
                if (cancelled) return;
                setActiveAssignments(
                    (rows || []).filter((a) => a.is_active),
                );
            })
            .catch(() => !cancelled && setActiveAssignments([]))
            .finally(() => !cancelled && setActiveLoading(false));
        return () => {
            cancelled = true;
        };
    }, [open, individualId]);

    // Filter caregivers to those sharing the individual's house
    const selectedIndividual = useMemo(
        () => individuals.find((i) => i.id === individualId),
        [individuals, individualId],
    );

    const busyCaregiverIds = useMemo(
        () => new Set(activeAssignments.map((a) => a.caregiver_id)),
        [activeAssignments],
    );

    const eligibleCaregivers = useMemo(() => {
        let list = caregivers;
        if (selectedIndividual) {
            list = list.filter(
                (c) =>
                    c.scope_type !== "house" ||
                    c.scope_id === selectedIndividual.house_id,
            );
        }
        if (busyCaregiverIds.size > 0) {
            list = list.filter((c) => !busyCaregiverIds.has(c.id));
        }
        return list;
    }, [caregivers, selectedIndividual, busyCaregiverIds]);

    // If the currently selected caregiver becomes ineligible after loading
    // active assignments, clear the field so the supervisor picks a valid one.
    useEffect(() => {
        if (
            caregiverId &&
            busyCaregiverIds.size > 0 &&
            busyCaregiverIds.has(caregiverId)
        ) {
            setCaregiverId("");
        }
    }, [caregiverId, busyCaregiverIds]);

    const invalidRange =
        effectiveTo && effectiveFrom && effectiveTo < effectiveFrom;

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!caregiverId || !individualId || !effectiveFrom) {
            toast.error("Complete caregiver, individual, and start date");
            return;
        }
        if (invalidRange) {
            toast.error("End date must be on/after start date");
            return;
        }
        setSaving(true);
        try {
            const result = await assignmentsApi.create({
                caregiver_id: caregiverId,
                individual_id: individualId,
                role,
                effective_from: effectiveFrom,
                effective_to: effectiveTo || null,
                notes: notes.trim() || null,
            });
            toast.success("Assignment created");
            onSaved?.(result);
            onOpenChange(false);
        } catch (err) {
            const msg = err?.message || "Create failed";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-40 flex items-end justify-center bg-slate-900/50 p-2 backdrop-blur-sm sm:items-center sm:p-4"
            data-testid="assignment-form-drawer"
        >
            <form
                onSubmit={submit}
                className="flex max-h-[95vh] w-full max-w-lg flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                    <h3 className="inline-flex items-center gap-2 text-base font-heading font-bold text-slate-900">
                        <UserPlus className="h-4 w-4 text-brand-blue-700" />
                        New Assignment
                    </h3>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        data-testid="assignment-form-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="min-w-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                    {/* Individual */}
                    <div>
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            Individual *
                        </label>
                        <select
                            required
                            value={individualId}
                            onChange={(e) => setIndividualId(e.target.value)}
                            disabled={!!defaultIndividualId}
                            data-testid="assignment-form-individual-select"
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30 disabled:bg-slate-50"
                        >
                            <option value="">Select an individual…</option>
                            {individuals.map((i) => (
                                <option key={i.id} value={i.id}>
                                    {`${i.first_name} ${i.last_name}`}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Caregiver */}
                    <div>
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            Caregiver *
                        </label>
                        {/* Current active assignees — context for the supervisor */}
                        {individualId && activeAssignments.length > 0 && (
                            <div
                                className="mb-2 rounded-lg border border-brand-blue-200 bg-brand-blue-50 px-3 py-2"
                                data-testid="assignment-form-current-active"
                            >
                                <div className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-brand-blue-800">
                                    <Info className="h-3 w-3" />
                                    Already assigned to this individual
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {activeAssignments.map((a) => (
                                        <div
                                            key={a.id}
                                            className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-700 shadow-sm"
                                        >
                                            <span className="font-semibold">
                                                {a.caregiver_name}
                                            </span>
                                            <AssignmentRoleBadge
                                                role={a.role}
                                                size="xs"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <select
                            required
                            value={caregiverId}
                            onChange={(e) => setCaregiverId(e.target.value)}
                            disabled={activeLoading}
                            data-testid="assignment-form-caregiver-select"
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30 disabled:opacity-60"
                        >
                            <option value="">
                                {activeLoading
                                    ? "Loading available caregivers…"
                                    : "Select a caregiver…"}
                            </option>
                            {eligibleCaregivers.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {`${c.first_name} ${c.last_name}`}{" "}
                                    ({c.email})
                                </option>
                            ))}
                        </select>
                        {!activeLoading &&
                            selectedIndividual &&
                            eligibleCaregivers.length === 0 && (
                                <p
                                    className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-800"
                                    data-testid="assignment-form-no-eligible"
                                >
                                    {activeAssignments.length > 0
                                        ? "All caregivers of this house are already actively assigned to this individual. End an existing assignment first to reassign."
                                        : "No caregivers assigned to this individual's house."}
                                </p>
                            )}
                    </div>

                    {/* Role */}
                    <div>
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            Role *
                        </label>
                        <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
                            {ROLES.map((r) => {
                                const active = role === r.value;
                                return (
                                    <button
                                        key={r.value}
                                        type="button"
                                        onClick={() => setRole(r.value)}
                                        data-testid={`assignment-form-role-${r.value}`}
                                        data-active={active || undefined}
                                        className={`flex flex-col items-center rounded-md px-2 py-1.5 text-xs font-bold transition-colors ${
                                            active
                                                ? "bg-white text-brand-blue-700 shadow-sm"
                                                : "text-slate-500 hover:text-slate-700"
                                        }`}
                                    >
                                        <span>{r.label}</span>
                                        <span className="text-[9px] font-normal text-slate-400">
                                            {r.hint}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                                Effective from *
                            </label>
                            <DatePickerUS
                                value={effectiveFrom}
                                onChange={(iso) => iso && setEffectiveFrom(iso)}
                                testId="assignment-form-from"
                                className="h-10 w-full"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                                Effective to <span className="normal-case text-slate-400">(optional)</span>
                            </label>
                            <DatePickerUS
                                value={effectiveTo}
                                onChange={setEffectiveTo}
                                min={effectiveFrom}
                                testId="assignment-form-to"
                                className="h-10 w-full"
                                clearable
                            />
                        </div>
                    </div>
                    {invalidRange && (
                        <p
                            className="text-[11px] font-bold text-red-700"
                            data-testid="assignment-form-range-error"
                        >
                            End date must be on/after start date.
                        </p>
                    )}
                    <p className="text-[11px] text-slate-500">
                        {effectiveTo
                            ? `Fixed period: ${formatDateUS(effectiveFrom)} → ${formatDateUS(effectiveTo)}`
                            : "Ongoing assignment (no end date set)"}
                    </p>

                    {/* Notes */}
                    <div>
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-slate-500">
                            Notes <span className="normal-case text-slate-400">(optional)</span>
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            data-testid="assignment-form-notes"
                            placeholder="e.g. Rotation coverage while primary is on leave"
                            className="w-full resize-y rounded-lg border border-slate-200 bg-white p-2.5 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                        data-testid="assignment-form-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={
                            saving ||
                            !caregiverId ||
                            !individualId ||
                            !effectiveFrom ||
                            !!invalidRange
                        }
                        data-testid="assignment-form-submit"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-blue-700 px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <UserPlus className="h-4 w-4" />
                        )}
                        {saving ? "Saving…" : "Create assignment"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AssignmentFormDrawer;
